import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl, getImageUrl, BACKEND_URL } from '../utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import FileUpload from '../components/FileUpload';

const API = getBackendUrl();

const AdminBookingEvents = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showAttendeesModal, setShowAttendeesModal] = useState(null);
  const [attendees, setAttendees] = useState(null);
  const [activeTab, setActiveTab] = useState('events'); // 'events', 'bookings', or 'archived'
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'confirmed', 'cancelled', 'expired'
  const [eventFilter, setEventFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [movePlayerModal, setMovePlayerModal] = useState(null);
  const [selectedNewTeam, setSelectedNewTeam] = useState('');
  const [selectedNewEvent, setSelectedNewEvent] = useState('');
  const [markPaidModal, setMarkPaidModal] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [waitlistModal, setWaitlistModal] = useState(null);
  const [waitlistData, setWaitlistData] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [manualSignupModal, setManualSignupModal] = useState(null);
  const [manualSignupEmail, setManualSignupEmail] = useState('');
  const [manualSignupTeamId, setManualSignupTeamId] = useState('');
  const [manualSignupNotes, setManualSignupNotes] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [playerProfile, setPlayerProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    date: '',
    end_date: '',
    time: '',
    end_time: '',
    location: '',
    location_details: '',
    price: '',
    image_url: '',
    is_active: true,
    teams: []
  });

  const [newTeam, setNewTeam] = useState({
    name: '',
    capacity: '',
    description: '',
    colour: ''
  });

  useEffect(() => {
    fetchEvents();
    fetchArchivedEvents();
  }, []);

  useEffect(() => {
    if (activeTab === 'bookings') {
      fetchBookings();
    }
  }, [activeTab]);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API}/admin/booking/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedEvents = async () => {
    try {
      const response = await fetch(`${API}/admin/booking/events/archived`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setArchivedEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching archived events:', error);
    }
  };

  const fetchBookings = async () => {
    setBookingsLoading(true);
    try {
      const response = await fetch(`${API}/admin/booking/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBookings(data.bookings);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTeamInputChange = (e) => {
    const { name, value } = e.target;
    setNewTeam(prev => ({ ...prev, [name]: value }));
  };

  const addTeam = () => {
    if (!newTeam.name || !newTeam.capacity) {
      toast.error('Team name and capacity are required');
      return;
    }
    const newTeamWithId = {
      ...newTeam, 
      id: `new-${Date.now()}`,  // Temporary ID for new teams
      capacity: parseInt(newTeam.capacity),
      current_count: 0
    };
    setFormData(prev => ({
      ...prev,
      teams: [...prev.teams, newTeamWithId]
    }));
    setNewTeam({ name: '', capacity: '', description: '', colour: '' });
  };

  const removeTeam = (index) => {
    const team = formData.teams[index];
    if (team.current_count > 0) {
      if (!window.confirm(`This team has ${team.current_count} booked players. Are you sure you want to remove it?`)) {
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      teams: prev.teams.filter((_, i) => i !== index)
    }));
  };

  const updateTeam = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      teams: prev.teams.map((team, i) => 
        i === index ? { ...team, [field]: value } : team
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.teams.length === 0 && !editingEvent) {
      toast.error('At least one team is required');
      return;
    }

    try {
      const url = editingEvent 
        ? `${API}/admin/booking/events/${editingEvent.id}`
        : `${API}/admin/booking/events`;
      
      const method = editingEvent ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        price: parseFloat(formData.price)
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(editingEvent ? 'Event updated' : 'Event created');
        fetchEvents();
        resetForm();
      } else {
        toast.error(data.detail || 'Failed to save event');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name || '',
      description: event.description || '',
      date: event.date || '',
      end_date: event.end_date || '',
      time: event.time || '',
      end_time: event.end_time || '',
      location: event.location || '',
      location_details: event.location_details || '',
      price: event.price?.toString() || '',
      image_url: event.image_url || '',
      is_active: event.is_active ?? true,
      teams: event.teams || []
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) return;

    try {
      const response = await fetch(`${API}/admin/booking/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Event deleted');
        fetchEvents();
      } else {
        toast.error(data.detail || 'Failed to delete event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleArchive = async (eventId, eventName) => {
    if (!window.confirm(`Archive "${eventName}"? This will hide it from active events but preserve all booking history.`)) return;

    try {
      const response = await fetch(`${API}/admin/booking/events/${eventId}/archive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Event archived');
        fetchEvents();
        fetchArchivedEvents();
      } else {
        toast.error(data.detail || 'Failed to archive event');
      }
    } catch (error) {
      console.error('Error archiving event:', error);
      toast.error('Failed to archive event');
    }
  };

  const handleUnarchive = async (eventId, eventName) => {
    if (!window.confirm(`Restore "${eventName}" from archive?`)) return;

    try {
      const response = await fetch(`${API}/admin/booking/events/${eventId}/unarchive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Event restored');
        fetchEvents();
        fetchArchivedEvents();
      } else {
        toast.error(data.detail || 'Failed to restore event');
      }
    } catch (error) {
      console.error('Error restoring event:', error);
      toast.error('Failed to restore event');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking? This will release the slot and notify the customer.')) return;

    try {
      const response = await fetch(`${API}/admin/booking/bookings/${bookingId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Booking cancelled and slot released');
        fetchBookings();
        fetchEvents(); // Refresh event capacity
      } else {
        toast.error(data.detail || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  const handleRemovePending = async (bookingId) => {
    if (!window.confirm('Remove this pending booking? This will release the slot (no email sent to customer).')) return;

    try {
      const response = await fetch(`${API}/admin/booking/bookings/${bookingId}/remove-pending`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Pending booking removed and slot released');
        fetchBookings();
        fetchEvents();
      } else {
        toast.error(data.detail || 'Failed to remove pending booking');
      }
    } catch (error) {
      console.error('Error removing pending booking:', error);
      toast.error('Failed to remove pending booking');
    }
  };

  const handleBulkRemovePending = async () => {
    const pendingCount = bookings.filter(b => b.status === 'pending').length;
    if (pendingCount === 0) {
      toast.info('No pending bookings to remove');
      return;
    }
    
    if (!window.confirm(`Remove ALL ${pendingCount} pending booking(s)? This will release all slots (no emails sent).`)) return;

    try {
      const response = await fetch(`${API}/admin/booking/bookings/bulk-remove-pending`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Removed ${data.removed_count} pending booking(s)`);
        fetchBookings();
        fetchEvents();
      } else {
        toast.error(data.detail || 'Failed to bulk remove pending bookings');
      }
    } catch (error) {
      console.error('Error bulk removing pending bookings:', error);
      toast.error('Failed to bulk remove pending bookings');
    }
  };

  const handleMoveToTeam = async () => {
    if (!movePlayerModal || !selectedNewTeam) {
      toast.error('Please select a team');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/booking/bookings/${movePlayerModal.booking_id}/move-team?new_team_id=${selectedNewTeam}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        setMovePlayerModal(null);
        setSelectedNewTeam('');
        setSelectedNewEvent('');
        if (showAttendeesModal) {
          fetchAttendees(showAttendeesModal);
        }
        fetchEvents();
      } else {
        toast.error(data.detail || 'Failed to move player');
      }
    } catch (error) {
      console.error('Error moving player:', error);
      toast.error('Failed to move player');
    }
  };

  const handleMoveToEvent = async () => {
    if (!movePlayerModal || !selectedNewEvent || !selectedNewTeam) {
      toast.error('Please select an event and team');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/booking/bookings/${movePlayerModal.booking_id}/move-event?new_event_id=${selectedNewEvent}&new_team_id=${selectedNewTeam}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        setMovePlayerModal(null);
        setSelectedNewTeam('');
        setSelectedNewEvent('');
        if (showAttendeesModal) {
          fetchAttendees(showAttendeesModal);
        }
        fetchEvents();
      } else {
        toast.error(data.detail || 'Failed to move player');
      }
    } catch (error) {
      console.error('Error moving player:', error);
      toast.error('Failed to move player');
    }
  };

  const handleMarkAsPaid = async () => {
    if (!markPaidModal) return;

    try {
      const params = new URLSearchParams();
      params.append('payment_method', paymentMethod);
      if (paymentReference) {
        params.append('payment_reference', paymentReference);
      }

      const response = await fetch(`${API}/admin/booking/bookings/${markPaidModal.id}/mark-paid?${params.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Booking confirmed - Ticket: ${data.ticket_number}`);
        setMarkPaidModal(null);
        setPaymentMethod('cash');
        setPaymentReference('');
        fetchBookings();
        fetchEvents();
      } else {
        toast.error(data.detail || 'Failed to mark as paid');
      }
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error('Failed to mark as paid');
    }
  };

  const fetchAttendees = async (event) => {
    try {
      const response = await fetch(`${API}/admin/booking/events/${event.id}/attendees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAttendees(data);
        setShowAttendeesModal(event);
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
      toast.error('Failed to load attendees');
    }
  };

  const fetchPlayerProfile = async (bookingId) => {
    setLoadingProfile(true);
    try {
      const response = await fetch(`${API}/admin/booking/bookings/${bookingId}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPlayerProfile(data.profile);
      } else {
        toast.error('Failed to load player profile');
      }
    } catch (error) {
      console.error('Error fetching player profile:', error);
      toast.error('Failed to load player profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchWaitlist = async (event) => {
    setWaitlistLoading(true);
    setWaitlistModal(event);
    try {
      const response = await fetch(`${API}/admin/booking/events/${event.id}/waitlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWaitlistData(data.waitlist || []);
      }
    } catch (error) {
      console.error('Error fetching waitlist:', error);
      toast.error('Failed to load waitlist');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleNotifyWaitlist = async (eventId) => {
    try {
      const response = await fetch(`${API}/admin/booking/events/${eventId}/waitlist/notify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.notified_count > 0) {
          toast.success(`Notified ${data.notified_count} people on the waitlist`);
        } else {
          toast.info(data.message || 'No one to notify');
        }
        fetchEvents();
      } else {
        throw new Error('Failed to notify waitlist');
      }
    } catch (error) {
      console.error('Error notifying waitlist:', error);
      toast.error('Failed to notify waitlist');
    }
  };

  const handleRemoveFromWaitlist = async (waitlistId) => {
    if (!confirm('Remove this person from the waitlist?')) return;
    try {
      const response = await fetch(`${API}/admin/booking/waitlist/${waitlistId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Removed from waitlist');
        // Refresh waitlist
        if (waitlistModal) {
          fetchWaitlist(waitlistModal);
        }
        fetchEvents();
      }
    } catch (error) {
      console.error('Error removing from waitlist:', error);
      toast.error('Failed to remove from waitlist');
    }
  };

  const handleSendManualSignup = async () => {
    if (!manualSignupEmail || !manualSignupTeamId) {
      toast.error('Please enter an email and select a team');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await fetch(`${API}/admin/booking/events/${manualSignupModal.id}/manual-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_id: manualSignupModal.id,
          team_id: manualSignupTeamId,
          email: manualSignupEmail,
          notes: manualSignupNotes || null
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Invite sent to ${manualSignupEmail}`);
        setManualSignupModal(null);
        setManualSignupEmail('');
        setManualSignupTeamId('');
        setManualSignupNotes('');
      } else {
        toast.error(data.detail || 'Failed to send invite');
      }
    } catch (error) {
      console.error('Error sending manual signup:', error);
      toast.error('Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      name: '',
      description: '',
      date: '',
      end_date: '',
      time: '',
      end_time: '',
      location: '',
      location_details: '',
      price: '',
      image_url: '',
      is_active: true,
      teams: []
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '-';
    const start = new Date(startDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    if (!endDate || endDate === startDate) return start;
    const end = new Date(endDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    return `${start} - ${end}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // Filter bookings based on search and filters
  const filteredBookings = bookings.filter(booking => {
    // Status filter
    if (statusFilter !== 'all' && booking.status !== statusFilter) return false;
    
    // Event filter
    if (eventFilter !== 'all' && booking.event_id !== eventFilter) return false;
    
    // Search filter (name, email, ticket number, event name)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = booking.customer?.name?.toLowerCase().includes(query);
      const matchesEmail = booking.customer?.email?.toLowerCase().includes(query);
      const matchesTicket = booking.ticket_number?.toLowerCase().includes(query);
      const matchesEvent = booking.event_name?.toLowerCase().includes(query);
      if (!matchesName && !matchesEmail && !matchesTicket && !matchesEvent) return false;
    }
    
    return true;
  });

  // Get unique events for filter dropdown
  const uniqueEvents = [...new Map(bookings.map(b => [b.event_id, { id: b.event_id, name: b.event_name }])).values()];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl md:text-2xl font-bold text-[#c5bfa9]">Events & Bookings</h1>
        
        {/* Tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('events')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'events' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Events
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'bookings' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'archived' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Archived {archivedEvents.length > 0 && `(${archivedEvents.length})`}
          </button>
        </div>
      </div>

      {/* Events Tab */}
      {activeTab === 'events' && (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg text-sm"
            >
              + Create Event
            </button>
          </div>

          {/* Events List - Mobile Cards */}
          <div className="space-y-4 md:hidden">
            {events.map((event) => (
              <div key={event.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900">{event.name}</h3>
                    <p className="text-sm text-gray-500">{event.date} {event.time && `• ${event.time}`}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {event.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-gray-500">{event.location}</span>
                  <span className="font-medium">£{event.price?.toFixed(2)}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-gray-500">{event.teams?.length || 0} teams</span>
                  <span>
                    <span className="font-900 text-gray-900">{event.total_booked || 0}</span>
                    <span className="text-gray-500"> / {event.total_capacity || 0} booked</span>
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  <button
                    onClick={() => fetchAttendees(event)}
                    className="flex-1 px-3 py-2 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                  >
                    Attendees
                  </button>
                  <button
                    onClick={() => {
                      setManualSignupModal(event);
                      setManualSignupTeamId(event.teams?.[0]?.id || '');
                    }}
                    className="flex-1 px-3 py-2 text-sm text-green-600 bg-green-50 rounded hover:bg-green-100"
                  >
                    + Invite
                  </button>
                  <button
                    onClick={() => handleEdit(event)}
                    className="flex-1 px-3 py-2 text-sm text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleArchive(event.id, event.name)}
                    className="px-3 py-2 text-sm text-amber-600 bg-amber-50 rounded hover:bg-amber-100"
                  >
                    Archive
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    className="px-3 py-2 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                No events yet. Create your first event!
              </div>
            )}
          </div>

          {/* Events Table - Desktop */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Waitlist</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{event.name}</div>
                        <div className="text-sm text-gray-500">{event.teams?.length || 0} teams</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {event.date}
                        {event.time && <span className="text-gray-500 ml-1">{event.time}</span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{event.location}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">£{event.price?.toFixed(2)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className="font-bold text-gray-900">{event.total_booked || 0}</span>
                        <span className="text-gray-500"> / {event.total_capacity || 0}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {event.waitlist_count > 0 ? (
                          <button 
                            onClick={() => fetchWaitlist(event)}
                            className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full hover:bg-yellow-200"
                          >
                            {event.waitlist_count} waiting
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {event.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm space-x-2">
                        <button onClick={() => fetchAttendees(event)} className="text-blue-600 hover:text-blue-900">
                          Attendees
                        </button>
                        <button 
                          onClick={() => {
                            setManualSignupModal(event);
                            setManualSignupTeamId(event.teams?.[0]?.id || '');
                          }} 
                          className="text-green-600 hover:text-green-900"
                        >
                          + Invite
                        </button>
                        <button onClick={() => handleEdit(event)} className="text-indigo-600 hover:text-indigo-900">
                          Edit
                        </button>
                        <button onClick={() => handleArchive(event.id, event.name)} className="text-amber-600 hover:text-amber-900">
                          Archive
                        </button>
                        <button onClick={() => handleDelete(event.id)} className="text-red-600 hover:text-red-900">
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        No events yet. Create your first event!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Archived Tab */}
      {activeTab === 'archived' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Archived Events</h2>
            <p className="text-sm text-gray-500 mt-1">These events are hidden from the public but all booking history is preserved.</p>
          </div>
          
          {archivedEvents.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No archived events
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {archivedEvents.map((event) => (
                <div key={event.id} className="px-4 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{event.name}</h3>
                    <p className="text-sm text-gray-500">{event.date} • {event.location}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {event.confirmed_bookings || 0} confirmed bookings preserved
                    </p>
                  </div>
                  <button
                    onClick={() => handleUnarchive(event.id, event.name)}
                    className="px-4 py-2 text-sm text-green-600 bg-green-50 rounded hover:bg-green-100"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <>
          {/* Bookings Filters & Controls */}
          <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, ticket, or event..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-sm text-gray-900 bg-white placeholder-gray-400"
              />
            </div>
            
            {/* Filters Row */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
              
              {/* Event Filter */}
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent"
              >
                <option value="all">All Events</option>
                {uniqueEvents.map(event => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
              
              {/* Clear Filters */}
              {(statusFilter !== 'all' || eventFilter !== 'all' || searchQuery) && (
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setEventFilter('all');
                    setSearchQuery('');
                  }}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Clear Filters
                </button>
              )}
              
              {/* Spacer */}
              <div className="flex-1"></div>
              
              {/* Bulk Remove */}
              {filteredBookings.filter(b => b.status === 'pending').length > 0 && (
                <button
                  onClick={handleBulkRemovePending}
                  className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium whitespace-nowrap"
                >
                  Remove All Pending ({filteredBookings.filter(b => b.status === 'pending').length})
                </button>
              )}
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-gray-500 pt-2 border-t">
              <span className="text-gray-700">Showing {filteredBookings.length} of {bookings.length} bookings</span>
              <span className="text-gray-300">|</span>
              <span className="text-yellow-600">{bookings.filter(b => b.status === 'pending').length} pending</span>
              <span className="text-green-600">{bookings.filter(b => b.status === 'confirmed').length} confirmed</span>
            </div>
          </div>

          {bookingsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Bookings List - Mobile Cards */}
              <div className="space-y-4 md:hidden">
                {filteredBookings.map((booking) => (
                  <div key={booking.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-mono text-sm font-bold text-emerald-700">
                          {booking.ticket_number || 'Pending...'}
                        </p>
                        <p className="font-medium text-gray-900">{booking.customer?.name}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-1">{booking.customer?.email}</p>
                    <p className="text-sm text-gray-500 mb-3">{booking.customer?.phone}</p>
                    
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-gray-600">{booking.event_name}</span>
                      <span className="font-medium">£{booking.amount?.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-gray-500">Team: {booking.team_name || '-'}</span>
                      <span className="text-gray-400">{formatDateTime(booking.created_at)}</span>
                    </div>
                    
                    {booking.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => setMarkPaidModal(booking)}
                          className="flex-1 px-3 py-2 text-sm text-green-600 bg-green-50 rounded hover:bg-green-100"
                        >
                          Mark Paid
                        </button>
                        <button
                          onClick={() => handleRemovePending(booking.id)}
                          className="flex-1 px-3 py-2 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {booking.status === 'confirmed' && (
                      <button
                        onClick={() => handleCancelBooking(booking.id)}
                        className="w-full mt-3 px-3 py-2 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100"
                      >
                        Cancel Booking
                      </button>
                    )}
                  </div>
                ))}
                {filteredBookings.length === 0 && (
                  <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                    {bookings.length === 0 ? 'No bookings yet' : 'No bookings match your filters'}
                  </div>
                )}
              </div>

              {/* Bookings Table - Desktop */}
              <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredBookings.map((booking) => (
                        <tr key={booking.id}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="font-mono text-sm font-bold text-emerald-700">
                              {booking.ticket_number || 'Pending...'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{booking.customer?.name}</div>
                            <div className="text-sm text-gray-500">{booking.customer?.email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {booking.event_name || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {booking.team_name || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            £{booking.amount?.toFixed(2) || '0.00'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(booking.status)}`}>
                              {booking.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDateTime(booking.created_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                            {booking.status === 'pending' && (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setMarkPaidModal(booking)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  Mark Paid
                                </button>
                                <button
                                  onClick={() => handleRemovePending(booking.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                            {booking.status === 'confirmed' && (
                              <button
                                onClick={() => handleCancelBooking(booking.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {filteredBookings.length === 0 && (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                            {bookings.length === 0 ? 'No bookings yet' : 'No bookings match your filters'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8">
            <div className="p-4 md:p-6 border-b flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                {editingEvent ? 'Edit Event' : 'Create Event'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date <span className="text-gray-400 text-xs">(multi-day)</span></label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    min={formData.date}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (£) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Image</label>
                  <FileUpload
                    currentFile={formData.image_url}
                    onFileUploaded={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="mr-2 w-4 h-4 text-[#5a6f4e] rounded"
                    />
                    <span className="text-sm text-gray-700">Active (visible to customers)</span>
                  </label>
                </div>
              </div>

              {/* Teams Section */}
              <div className="border-t pt-4 md:pt-6">
                <h3 className="font-medium text-gray-900 mb-4">
                  Teams / Sides
                  {editingEvent && <span className="text-sm font-normal text-gray-500 ml-2">(Edit existing or add new teams)</span>}
                </h3>
                
                {/* Existing Teams */}
                {formData.teams.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {formData.teams.map((team, index) => (
                      <div key={team.id || index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Team Name</label>
                            <input
                              type="text"
                              value={team.name}
                              onChange={(e) => updateTeam(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Capacity</label>
                            <input
                              type="number"
                              value={team.capacity}
                              onChange={(e) => updateTeam(index, 'capacity', parseInt(e.target.value) || 0)}
                              min="0"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                            <input
                              type="text"
                              value={team.description || ''}
                              onChange={(e) => updateTeam(index, 'description', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white"
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            {editingEvent && team.current_count > 0 && (
                              <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Booked</label>
                                <span className="inline-block px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                                  {team.current_count} / {team.capacity}
                                </span>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => removeTeam(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm border border-red-200"
                              title={team.current_count > 0 ? "Warning: This team has bookings" : "Remove team"}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add Team Form */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-green-50 p-3 rounded-lg border border-green-200">
                  <input
                    type="text"
                    name="name"
                    value={newTeam.name}
                    onChange={handleTeamInputChange}
                    placeholder="New team name"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400"
                  />
                  <input
                    type="number"
                    name="capacity"
                    value={newTeam.capacity}
                    onChange={handleTeamInputChange}
                    placeholder="Capacity"
                    min="1"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400"
                  />
                  <input
                    type="text"
                    name="description"
                    value={newTeam.description}
                    onChange={handleTeamInputChange}
                    placeholder="Description"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hidden md:block text-gray-900 bg-white placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={addTeam}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
                  >
                    + Add Team
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg"
                >
                  {editingEvent ? 'Save Changes' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attendees Modal */}
      {showAttendeesModal && attendees && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-4xl my-8">
            <div className="p-4 md:p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900">{attendees.event_name}</h2>
                <p className="text-sm text-gray-500">{attendees.event_date} • {attendees.total_attendees} attendees</p>
              </div>
              <button
                onClick={() => setShowAttendeesModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 md:p-6 max-h-[70vh] overflow-y-auto">
              {attendees.teams?.map((team) => (
                <div key={team.team_id} className="mb-6">
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">
                    {team.team_name}
                    <span className="text-gray-500 font-normal ml-2">
                      ({team.attendees.length} / {team.capacity})
                    </span>
                  </h3>
                  
                  {team.attendees.length > 0 ? (
                    <>
                      {/* Mobile Cards */}
                      <div className="space-y-3 md:hidden">
                        {team.attendees.map((attendee) => (
                          <div key={attendee.booking_id} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-start justify-between mb-2">
                              <p className="font-mono text-sm font-bold text-emerald-700">
                                {attendee.ticket_number}
                              </p>
                              <div className="flex gap-2">
                                {attendee.has_waiver && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const response = await fetch(`${API}/admin/booking/waivers/download/${attendee.booking_id}`, {
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        if (response.ok) {
                                          const blob = await response.blob();
                                          const url = window.URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `waiver_${attendee.ticket_number}.pdf`;
                                          document.body.appendChild(a);
                                          a.click();
                                          window.URL.revokeObjectURL(url);
                                          a.remove();
                                        } else {
                                          toast.error('Failed to download waiver');
                                        }
                                      } catch (error) {
                                        toast.error('Failed to download waiver');
                                      }
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    Waiver
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setMovePlayerModal({ 
                                      booking_id: attendee.booking_id, 
                                      customer_name: attendee.customer_name,
                                      current_team_id: team.team_id,
                                      current_event_id: showAttendeesModal.id
                                    });
                                    setSelectedNewTeam('');
                                    setSelectedNewEvent('');
                                  }}
                                  className="text-xs text-indigo-600 hover:text-indigo-800"
                                >
                                  Move
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Cancel booking for ${attendee.customer_name}? This will notify the customer.`)) {
                                      handleCancelBooking(attendee.booking_id);
                                      fetchAttendees(showAttendeesModal);
                                    }
                                  }}
                                  className="text-xs text-red-600 hover:text-red-800"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={() => fetchPlayerProfile(attendee.booking_id)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {attendee.customer_name}
                            </button>
                            <p className="text-sm text-gray-600">{attendee.customer_email}</p>
                          </div>
                        ))}
                      </div>
                      
                      {/* Desktop Table */}
                      <table className="w-full text-sm hidden md:table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-500">Ticket</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-500">Name</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-500">Email</th>
                            <th className="px-4 py-2 text-right font-medium text-gray-500">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {team.attendees.map((attendee) => (
                            <tr key={attendee.booking_id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-mono text-sm font-bold text-emerald-700">
                                {attendee.ticket_number}
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => fetchPlayerProfile(attendee.booking_id)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                  data-testid={`view-profile-${attendee.booking_id}`}
                                >
                                  {attendee.customer_name}
                                </button>
                              </td>
                              <td className="px-4 py-2 text-gray-900">{attendee.customer_email}</td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  {attendee.has_waiver && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          const response = await fetch(`${API}/admin/booking/waivers/download/${attendee.booking_id}`, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          if (response.ok) {
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `waiver_${attendee.ticket_number}_${attendee.customer_name.replace(/\s/g, '_')}.pdf`;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            a.remove();
                                          } else {
                                            toast.error('Failed to download waiver');
                                          }
                                        } catch (error) {
                                          console.error('Error downloading waiver:', error);
                                          toast.error('Failed to download waiver');
                                        }
                                      }}
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      Waiver
                                    </button>
                                  )}
                                  <button
                                    onClick={() => {
                                      setMovePlayerModal({ 
                                        booking_id: attendee.booking_id, 
                                        customer_name: attendee.customer_name,
                                        current_team_id: team.team_id,
                                        current_event_id: showAttendeesModal.id
                                      });
                                      setSelectedNewTeam('');
                                      setSelectedNewEvent('');
                                    }}
                                    className="text-indigo-600 hover:text-indigo-800 text-sm"
                                  >
                                    Move
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (window.confirm(`Cancel booking for ${attendee.customer_name}? This will notify the customer.`)) {
                                        handleCancelBooking(attendee.booking_id);
                                        fetchAttendees(showAttendeesModal);
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p className="text-gray-500 italic">No attendees yet</p>
                  )}
                </div>
              ))}
            </div>
            
            {/* Footer with Export Button */}
            {attendees.total_attendees > 0 && (
              <div className="p-4 md:p-6 border-t bg-gray-50 flex flex-wrap gap-3 items-center justify-between">
                <p className="text-sm text-gray-500">
                  {attendees.total_attendees} total attendees
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API}/admin/booking/events/${showAttendeesModal.id}/attendees/export-profiles`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `emergency_contacts_${attendees.event_name?.replace(/\s/g, '_')}_${attendees.event_date}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          a.remove();
                          toast.success('Emergency contacts exported');
                        } else {
                          toast.error('Failed to export');
                        }
                      } catch (error) {
                        console.error('Export error:', error);
                        toast.error('Failed to export');
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2 text-sm"
                    data-testid="export-emergency-contacts"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Export Emergency Contacts
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API}/admin/booking/events/${showAttendeesModal.id}/attendees/export`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `attendees_${attendees.event_name?.replace(/\s/g, '_')}_${attendees.event_date}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          a.remove();
                          toast.success('Attendees exported');
                        } else {
                          toast.error('Failed to export');
                        }
                      } catch (error) {
                        console.error('Export error:', error);
                        toast.error('Failed to export');
                      }
                    }}
                    className="px-4 py-2 bg-[#3d4a35] text-white rounded hover:bg-[#4a5d42] flex items-center gap-2 text-sm"
                    data-testid="export-attendees"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Full List
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Player Profile Modal */}
      {playerProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">Player Profile</h2>
                  <p className="text-sm text-gray-500 font-mono">{playerProfile.ticket_number}</p>
                </div>
                <button
                  onClick={() => setPlayerProfile(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 md:p-6 space-y-6">
              {/* Contact Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Contact Details</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Name</span>
                    <span className="font-medium text-gray-900">{playerProfile.customer?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <a href={`mailto:${playerProfile.customer?.email}`} className="font-medium text-blue-600 hover:underline">
                      {playerProfile.customer?.email}
                    </a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Phone</span>
                    <a href={`tel:${playerProfile.customer?.phone}`} className="font-medium text-blue-600 hover:underline">
                      {playerProfile.customer?.phone}
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Emergency Contact */}
              <div>
                <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Emergency Contact
                </h3>
                <div className="bg-red-50 rounded-lg p-4 space-y-2 border border-red-100">
                  {playerProfile.customer?.emergency_contact_name ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name</span>
                        <span className="font-medium text-gray-900">{playerProfile.customer?.emergency_contact_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone</span>
                        <a href={`tel:${playerProfile.customer?.emergency_contact_phone}`} className="font-medium text-red-600 hover:underline">
                          {playerProfile.customer?.emergency_contact_phone}
                        </a>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500 italic text-sm">No emergency contact on file</p>
                  )}
                </div>
              </div>
              
              {/* Event Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Event Details</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Event</span>
                    <span className="font-medium text-gray-900">{playerProfile.event?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date</span>
                    <span className="font-medium text-gray-900">{playerProfile.event?.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Team</span>
                    <span className="font-medium text-gray-900">{playerProfile.event?.team}</span>
                  </div>
                </div>
              </div>
              
              {/* Payment & Waiver */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment & Waiver</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount</span>
                    <span className="font-medium text-gray-900">
                      £{playerProfile.payment?.amount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Paid At</span>
                    <span className="font-medium text-gray-900">
                      {playerProfile.payment?.confirmed_at 
                        ? new Date(playerProfile.payment.confirmed_at).toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Waiver Signed</span>
                    <span className={`font-medium ${playerProfile.waiver?.has_waiver ? 'text-green-600' : 'text-red-600'}`}>
                      {playerProfile.waiver?.has_waiver ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {playerProfile.waiver?.acceptance && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Waiver Version</span>
                      <span className="font-medium text-gray-900">v{playerProfile.waiver.acceptance.version}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Notes */}
              {playerProfile.notes && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Notes</h3>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                    <p className="text-gray-700 text-sm">{playerProfile.notes}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 md:p-6 border-t bg-gray-50 flex gap-3">
              {playerProfile.waiver?.has_waiver && (
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/admin/booking/waivers/download/${playerProfile.booking_id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `waiver_${playerProfile.ticket_number}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                      } else {
                        toast.error('Failed to download waiver');
                      }
                    } catch (error) {
                      toast.error('Failed to download waiver');
                    }
                  }}
                  className="flex-1 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Waiver
                </button>
              )}
              <button
                onClick={() => setPlayerProfile(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Player Modal */}
      {movePlayerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Move Player</h2>
              <button
                onClick={() => {
                  setMovePlayerModal(null);
                  setSelectedNewTeam('');
                  setSelectedNewEvent('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-gray-600">Moving: <strong>{movePlayerModal.customer_name}</strong></p>
              
              {/* Move within same event */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <h3 className="font-medium text-gray-900 mb-2">Move to different team (same event)</h3>
                <select
                  value={selectedNewEvent === '' ? selectedNewTeam : ''}
                  onChange={(e) => {
                    setSelectedNewTeam(e.target.value);
                    setSelectedNewEvent('');
                  }}
                  className="w-full px-3 py-2 border rounded text-gray-900"
                >
                  <option value="">Select team...</option>
                  {showAttendeesModal?.id && events.find(e => e.id === showAttendeesModal.id)?.teams
                    .filter(t => t.id !== movePlayerModal.current_team_id)
                    .map(team => (
                      <option key={team.id} value={team.id} disabled={team.current_count >= team.capacity}>
                        {team.name} ({team.current_count}/{team.capacity})
                      </option>
                    ))
                  }
                </select>
                <button
                  onClick={handleMoveToTeam}
                  disabled={!selectedNewTeam || selectedNewEvent}
                  className="mt-2 w-full py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Move to Team
                </button>
              </div>
              
              {/* Move to different event */}
              <div className="border rounded-lg p-3">
                <h3 className="font-medium text-gray-900 mb-2">Move to different event</h3>
                <div className="space-y-2">
                  <select
                    value={selectedNewEvent}
                    onChange={(e) => {
                      setSelectedNewEvent(e.target.value);
                      setSelectedNewTeam('');
                    }}
                    className="w-full px-3 py-2 border rounded text-gray-900"
                  >
                    <option value="">Select event...</option>
                    {events
                      .filter(e => e.id !== movePlayerModal.current_event_id && e.is_active)
                      .map(event => (
                        <option key={event.id} value={event.id}>
                          {event.name} ({event.date})
                        </option>
                      ))
                    }
                  </select>
                  
                  {selectedNewEvent && (
                    <select
                      value={selectedNewTeam}
                      onChange={(e) => setSelectedNewTeam(e.target.value)}
                      className="w-full px-3 py-2 border rounded text-gray-900"
                    >
                      <option value="">Select team...</option>
                      {events.find(e => e.id === selectedNewEvent)?.teams.map(team => (
                        <option key={team.id} value={team.id} disabled={team.current_count >= team.capacity}>
                          {team.name} ({team.current_count}/{team.capacity})
                        </option>
                      ))}
                    </select>
                  )}
                  
                  <button
                    onClick={handleMoveToEvent}
                    disabled={!selectedNewEvent || !selectedNewTeam}
                    className="w-full py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Move to Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      {markPaidModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Mark as Paid</h2>
              <button
                onClick={() => {
                  setMarkPaidModal(null);
                  setPaymentMethod('cash');
                  setPaymentReference('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <p className="text-gray-600 mb-1">Customer: <strong>{markPaidModal.customer?.name}</strong></p>
                <p className="text-gray-600 mb-1">Event: <strong>{markPaidModal.event_name}</strong></p>
                <p className="text-gray-600">Amount: <strong>£{markPaidModal.amount?.toFixed(2)}</strong></p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-gray-900"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="paypal">PayPal</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reference (optional)</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="e.g. transfer ref, receipt number"
                  className="w-full px-3 py-2 border rounded text-gray-900"
                />
              </div>
              
              <button
                onClick={handleMarkAsPaid}
                className="w-full py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waitlist Modal */}
      {waitlistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8">
            <div className="p-4 md:p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900">Waitlist</h2>
                <p className="text-sm text-gray-500">{waitlistModal.name} • {waitlistData.length} waiting</p>
              </div>
              <button
                onClick={() => setWaitlistModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 md:p-6 max-h-[60vh] overflow-y-auto">
              {waitlistLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : waitlistData.length > 0 ? (
                <div className="space-y-3">
                  {waitlistData.map((entry, index) => (
                    <div key={entry.id} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{index + 1}. {entry.name}</p>
                        <p className="text-sm text-gray-500">{entry.email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Joined: {new Date(entry.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {entry.notified && <span className="ml-2 text-green-600">(Notified)</span>}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveFromWaitlist(entry.id)}
                        className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No one on the waitlist</p>
              )}
            </div>
            
            {waitlistData.length > 0 && (
              <div className="p-4 md:p-6 border-t bg-gray-50 flex gap-3">
                <button
                  onClick={() => handleNotifyWaitlist(waitlistModal.id)}
                  className="flex-1 py-2 bg-[#3d4a35] text-white rounded hover:bg-[#4a5d42]"
                >
                  Notify All (Spot Available)
                </button>
                <button
                  onClick={() => setWaitlistModal(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual Signup Modal */}
      {manualSignupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 md:p-6 border-b">
              <h2 className="text-lg font-bold text-gray-900">Send Free Ticket Invite</h2>
              <p className="text-sm text-gray-500 mt-1">{manualSignupModal.name}</p>
            </div>
            
            <div className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email *
                </label>
                <input
                  type="email"
                  value={manualSignupEmail}
                  onChange={(e) => setManualSignupEmail(e.target.value)}
                  placeholder="Enter email address..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team *
                </label>
                <select
                  value={manualSignupTeamId}
                  onChange={(e) => setManualSignupTeamId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                >
                  <option value="">Select a team...</option>
                  {manualSignupModal.teams?.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name} ({team.current_count || 0}/{team.capacity} booked)
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <input
                  type="text"
                  value={manualSignupNotes}
                  onChange={(e) => setManualSignupNotes(e.target.value)}
                  placeholder="Internal notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a6f4e] focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Free ticket:</strong> An email will be sent with a link to complete registration. 
                  No payment will be required.
                </p>
              </div>
            </div>
            
            <div className="p-4 md:p-6 border-t bg-gray-50 flex gap-3">
              <button
                onClick={handleSendManualSignup}
                disabled={sendingInvite || !manualSignupEmail || !manualSignupTeamId}
                className="flex-1 py-2 bg-[#3d4a35] text-white rounded hover:bg-[#4a5d42] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingInvite ? 'Sending...' : 'Send Invite'}
              </button>
              <button
                onClick={() => {
                  setManualSignupModal(null);
                  setManualSignupEmail('');
                  setManualSignupTeamId('');
                  setManualSignupNotes('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookingEvents;
